import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { socialMediaService } from '../../services/socialMediaService';
import NavigationBar from '../../components/ui/NavigationBar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CampaignDashboard from './components/CampaignDashboard';
import CampaignBuilder from './components/CampaignBuilder';
import SocialAnalytics from './components/SocialAnalytics';
import ContentLibrary from './components/ContentLibrary';

const SocialMediaMarketingHub = () => {
  const navigate = useNavigate();
  const { user, userProfile, isAdmin, isAdvisor } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCampaignBuilder, setShowCampaignBuilder] = useState(false);

  const breadcrumbItems = [
    { label: 'Inicio', path: '/', icon: 'Home' },
    { label: isAdmin ? 'Admin Dashboard' : 'Dashboard', path: isAdmin ? '/admin-dashboard' : '/user-dashboard', icon: 'LayoutDashboard' },
    { label: 'Marketing Hub', path: '/social-media-marketing-hub', icon: 'Share2' }
  ];

  useEffect(() => {
    if (user?.id) {
      loadCampaigns();
    }
  }, [user]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const campaignsData = await socialMediaService?.getCampaigns(user?.id);
      setCampaigns(campaignsData || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (campaignData) => {
    try {
      await socialMediaService?.createCampaign(campaignData, user?.id);
      await loadCampaigns();
      setShowCampaignBuilder(false);
    } catch (error) {
      console.error('Error creating campaign:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Cargando Marketing Hub...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />

      <div className="container mx-auto px-4 py-6 sm:py-8 mt-16 sm:mt-20">
        <BreadcrumbTrail items={breadcrumbItems} />

        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 flex items-center">
                <Icon name="Share2" size={32} className="mr-3 text-primary" />
                Social Media Marketing Hub
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Gestiona campañas en Facebook, Instagram, WhatsApp Business y LinkedIn
              </p>
            </div>
            <Button
              iconName="Plus"
              onClick={() => setShowCampaignBuilder(true)}
            >
              Nueva Campaña
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-2 mb-6 border-b border-border overflow-x-auto">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'dashboard' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="LayoutDashboard" size={18} className="inline mr-2" />
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'analytics' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="TrendingUp" size={18} className="inline mr-2" />
            Analíticas
          </button>
          <button
            onClick={() => setActiveTab('content')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'content' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Image" size={18} className="inline mr-2" />
            Biblioteca
          </button>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'dashboard' && (
            <CampaignDashboard campaigns={campaigns} onRefresh={loadCampaigns} />
          )}
          {activeTab === 'analytics' && (
            <SocialAnalytics campaigns={campaigns} />
          )}
          {activeTab === 'content' && (
            <ContentLibrary />
          )}
        </div>
      </div>

      {/* Campaign Builder Modal */}
      {showCampaignBuilder && (
        <CampaignBuilder
          onClose={() => setShowCampaignBuilder(false)}
          onSave={handleCreateCampaign}
        />
      )}
    </div>
  );
};

export default SocialMediaMarketingHub;
